import React, { useState, useEffect } from 'react';
import { rolesApi } from '../../api/roles.js';

// ─── Role icon ─────────────────────────────────────────────────────────────────
const getRoleIcon = (name) => {
  const n = name.toLowerCase();
  if (n.includes('admin')) return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" className="role-icon">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
  if (n.includes('accountant')) return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" className="role-icon">
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  );
  if (n.includes('sales')) return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" className="role-icon">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  );
  if (n.includes('crm')) return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" className="role-icon">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" className="role-icon">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
};

// MODULES and MODULE_GROUPS are now fetched from the server at runtime.

// ─── Level helpers ─────────────────────────────────────────────────────────────
function getLevel(mod, perms) {
  const s = new Set(perms);
  if (mod.full  && s.has(mod.full))  return 'full';
  if (mod.write && s.has(mod.write)) return 'write';
  if (mod.read  && s.has(mod.read))  return 'read';
  return 'none';
}

function applyLevel(mod, level, current) {
  const s = new Set(current);
  [mod.read, mod.write, mod.full].filter(Boolean).forEach(p => s.delete(p));
  if (level === 'read')  { s.add(mod.read); }
  if (level === 'write') { s.add(mod.read); if (mod.write) s.add(mod.write); }
  if (level === 'full')  { s.add(mod.read); if (mod.write) s.add(mod.write); if (mod.full) s.add(mod.full); }
  return [...s];
}

function displayRole(role) {
  if (!role) return '';
  const map = { admin: 'Admin', salesman: 'Salesman', teamleader: 'Team Leader', sales_teamlead: 'Team Leader', agent: 'Agent' };
  if (map[role]) return map[role];
  return role.split(/_|-/).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

// ─── Component ─────────────────────────────────────────────────────────────────
export default function RolesPage() {
  const [roles,         setRoles]         = useState([]);
  const [modules,       setModules]       = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [selectedRole,  setSelectedRole]  = useState(null);
  const [roleName,      setRoleName]      = useState('');
  const [selectedPerms,    setSelectedPerms]    = useState([]);
  const [isEditing,        setIsEditing]        = useState(false);
  const [saving,           setSaving]           = useState(false);
  const [err,              setErr]              = useState('');
  const [successMsg,       setSuccessMsg]       = useState('');

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const res = await rolesApi.getAll();
      setRoles(res.data ?? []);
    } catch (e) {
      console.error('Failed to load roles:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchMatrix = async () => {
    try {
      const res = await rolesApi.getMatrix();
      setModules(res.data ?? []);
    } catch (e) {
      console.error('Failed to load module matrix:', e);
    }
  };

  useEffect(() => { fetchRoles(); fetchMatrix(); }, []);

  const handleSelectRole = (role) => {
    setSelectedRole(role);
    setRoleName(role.name);
    setSelectedPerms(role.permissions || []);
    setIsEditing(true);
    setErr('');
    setSuccessMsg('');
  };

  const handleCreateNewClick = () => {
    setSelectedRole(null);
    setRoleName('');
    setSelectedPerms([]);
    setIsEditing(false);
    setErr('');
    setSuccessMsg('');
  };

  // Module radio handler: clicking the active level deselects (→ none)
  const handleModuleLevel = (mod, clickedLevel) => {
    const current = getLevel(mod, selectedPerms);
    const next = current === clickedLevel ? 'none' : clickedLevel;
    setSelectedPerms(prev => applyLevel(mod, next, prev));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!roleName.trim()) { setErr('Role name is required.'); return; }
    setSaving(true); setErr(''); setSuccessMsg('');
    try {
      const payload = { name: roleName.trim().toLowerCase(), permissions: selectedPerms };
      if (selectedRole) {
        await rolesApi.update(selectedRole.id, payload);
        setSuccessMsg('Role updated successfully!');
      } else {
        const res = await rolesApi.create(payload);
        setSuccessMsg('Role created successfully!');
        setSelectedRole(res.data ?? res);
      }
      await fetchRoles();
    } catch (e) {
      setErr(e.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedRole) return;
    if (!window.confirm(`Delete the role "${displayRole(selectedRole.name)}"?`)) return;
    setSaving(true); setErr(''); setSuccessMsg('');
    try {
      await rolesApi.remove(selectedRole.id);
      setSuccessMsg('Role deleted.');
      handleCreateNewClick();
      await fetchRoles();
    } catch (e) {
      setErr(e.message || 'Delete failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-body">
      <style>{`
        /* ── Roles list ── */
        .role-card-item {
          padding: 14px 18px;
          border-bottom: 1px solid #f1f5f9;
          cursor: pointer;
          background: #fff;
          transition: all 0.15s;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .role-card-item:hover  { background: #f8fafc; }
        .role-card-item.active { background: rgba(37,99,235,0.05); border-left: 4px solid var(--primary); }
        .role-icon             { opacity: 0.35; transition: opacity 0.15s; }
        .role-card-item.active .role-icon { opacity: 1; color: var(--primary); }

        /* ── Module table ── */
        .module-table { width: 100%; border-collapse: collapse; font-size: 13px; }
        .module-table thead th {
          padding: 10px 10px;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: #94a3b8;
          border-bottom: 2px solid #e2e8f0;
          text-align: center;
          white-space: nowrap;
          width: 100px;
        }
        .module-table thead th:first-child { text-align: left; padding-left: 16px; width: auto; }
        .group-row td {
          padding: 10px 0 4px 0;
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: #64748b;
          border-top: 1px solid #e2e8f0;
        }
        .group-row:first-of-type td { border-top: none; }
        .mod-row td {
          padding: 7px 10px;
          border-bottom: 1px solid #f8fafc;
          text-align: center;
          vertical-align: middle;
        }
        .mod-row td:first-child { text-align: left; padding-left: 12px; }
        .mod-row:hover { background: #fafbfc; }
        .mod-name {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 500;
          color: #1e293b;
          font-size: 13px;
        }
        .level-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          flex-shrink: 0;
          background: #e2e8f0;
        }

        /* ── Level buttons ── */
        .lvl-btn {
          display: inline-block;
          padding: 6px 0;
          width: 72px;
          border-radius: 7px;
          border: 1.5px solid #d1d5db;
          background: #f3f4f6;
          color: #6b7280;
          cursor: pointer;
          font-size: 12px;
          font-weight: 600;
          text-align: center;
          transition: all 0.12s;
          white-space: nowrap;
        }
        .lvl-btn:hover:not(:disabled) { border-color: #9ca3af; color: #374151; background: #e5e7eb; }
        .lvl-btn.lv-read  { border-color: #2563eb; color: #fff; background: #2563eb; }
        .lvl-btn.lv-write { border-color: #16a34a; color: #fff; background: #16a34a; }
        .lvl-btn.lv-full  { border-color: #d97706; color: #fff; background: #d97706; }
        .lvl-btn.lv-read:hover  { background: #1d4ed8; }
        .lvl-btn.lv-write:hover { background: #15803d; }
        .lvl-btn.lv-full:hover  { background: #b45309; }
        .lvl-btn:disabled { opacity: 0.28; cursor: not-allowed; }

        /* ── Form ── */
        .input-premium {
          width: 100%; max-width: 340px;
          padding: 9px 13px;
          border-radius: 8px;
          border: 1px solid #cbd5e1;
          font-size: 14px;
          transition: all 0.15s;
        }
        .input-premium:focus { outline: none; border-color: var(--primary); box-shadow: 0 0 0 3px rgba(37,99,235,0.1); }
      `}</style>

      {/* ── Unified single card ── */}
      <div className="card" style={{ overflow: 'hidden', display: 'flex', minHeight: 0 }}>

        {/* Left: roles list */}
        <div style={{ width: 240, flexShrink: 0, borderRight: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '16px 18px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-main)' }}>User Roles</span>
            <button className="btn btn-primary btn-xs" onClick={handleCreateNewClick} style={{ padding: '5px 11px', fontSize: '12px' }}>+ Add</button>
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Loading…</div>
            ) : (
              roles.filter(r => r.name !== 'admin').map(role => {
                const active = selectedRole?.id === role.id;
                return (
                  <div key={role.id} onClick={() => handleSelectRole(role)} className={`role-card-item ${active ? 'active' : ''}`}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13, color: active ? 'var(--primary)' : 'var(--text-main)' }}>
                        {displayRole(role.name)}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>
                        {role.permissions.includes('*') ? 'All permissions' : `${role.permissions.length} permission(s)`}
                      </div>
                    </div>
                    {getRoleIcon(role.name)}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Right: editor */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>

          {/* ── Fixed: header ── */}
          <div style={{ padding: '16px 24px', borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
            <span style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-main)' }}>
              {selectedRole ? `Configure Access — ${displayRole(selectedRole.name)}` : 'Create New Role'}
            </span>
          </div>

          <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>

            {/* ── Fixed: alerts + hint + new-role input ── */}
            <div style={{ padding: '14px 24px 0', flexShrink: 0 }}>
              {err && (
                <div style={{ marginBottom: 12, padding: '10px 14px', borderRadius: 8, background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', fontSize: 13 }}>{err}</div>
              )}
              {successMsg && (
                <div style={{ marginBottom: 12, padding: '10px 14px', borderRadius: 8, background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#16a34a', fontSize: 13 }}>{successMsg}</div>
              )}
              {!selectedRole && (
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontWeight: 600, fontSize: 13, color: 'var(--text-main)', marginBottom: 7 }}>
                    Role Identifier (lowercase, no spaces) *
                  </label>
                  <input
                    className="input-premium"
                    value={roleName}
                    onChange={e => setRoleName(e.target.value)}
                    placeholder="e.g. sales_manager"
                    disabled={saving}
                  />
                </div>
              )}
              <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
                Read — view only &nbsp;·&nbsp; Write — view + create/edit &nbsp;·&nbsp; Full — write + delete &nbsp;·&nbsp; Click active button to remove access.
              </p>
            </div>

            {/* ── SCROLLABLE: module table only ── */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px' }}>
              <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
                <table className="module-table">
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', paddingLeft: 16 }}>Module</th>
                      <th>Read</th>
                      <th>Write</th>
                      <th>Full</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...new Set(modules.map(m => m.group))].map(group => (
                      <React.Fragment key={group}>
                        <tr className="group-row">
                          <td colSpan={4} style={{ paddingLeft: 16 }}>{group}</td>
                        </tr>
                        {modules.filter(m => m.group === group).map(mod => {
                          const level = getLevel(mod, selectedPerms);
                          const dotColor = level === 'read' ? '#2563eb' : level === 'write' ? '#16a34a' : level === 'full' ? '#d97706' : '#e2e8f0';
                          return (
                            <tr key={mod.key} className="mod-row">
                              <td>
                                <span className="mod-name">
                                  <span className="level-dot" style={{ background: dotColor }} />
                                  {mod.label}
                                </span>
                              </td>
                              {['read', 'write', 'full'].map(lvl => (
                                <td key={lvl}>
                                  <button
                                    type="button"
                                    className={`lvl-btn ${level === lvl ? `lv-${lvl}` : ''}`}
                                    disabled={saving || (lvl === 'full' && !mod.full)}
                                    onClick={() => handleModuleLevel(mod, lvl)}
                                  >
                                    {lvl.charAt(0).toUpperCase() + lvl.slice(1)}
                                  </button>
                                </td>
                              ))}
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ── Fixed: footer buttons ── */}
            <div style={{ padding: '16px 24px', borderTop: '1px solid #e2e8f0', flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {selectedRole ? (
                <button
                  type="button"
                  style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', padding: '9px 16px', borderRadius: 8, fontWeight: 500, cursor: 'pointer' }}
                  onClick={handleDelete}
                  disabled={saving}
                >
                  Delete Role
                </button>
              ) : <div />}
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" className="btn btn-outline" style={{ padding: '9px 16px', borderRadius: 8 }} onClick={handleCreateNewClick} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" style={{ padding: '9px 20px', borderRadius: 8 }} disabled={saving}>
                  {saving ? 'Saving…' : 'Save Role'}
                </button>
              </div>
            </div>

          </form>
        </div>

      </div>
    </div>
  );
}
